import React from 'react';

/**
 * SnapScoreSummary
 * ----------------
 * Read-only SNAP-IV 26 surfacing for the ADHD triage decision wizard (Step 1).
 * Shows BOTH respondents side by side — parent/carer (formInstances
 * snapIV26_guardian) and school/teacher (formInstances snapIV26_school_user) —
 * with the per-subscale score, maximum and clinical-threshold state from the
 * stored `scaleResults` (the same data source NeurodevelopmentalScalesCard on
 * the patient overview reads). A respondent without complete results renders a
 * clear "Not completed" state.
 *
 * `buildSnapSnapshot` produces the snapSnapshot object persisted with the
 * decision — the exact numbers displayed here — so the audit record always
 * matches what the clinician saw.
 *
 * Props:
 *   formInstances — object of form instances keyed by instance id
 */

const GUARDIAN_INSTANCE_ID = 'snapIV26_guardian';
const SCHOOL_INSTANCE_ID = 'snapIV26_school_user';

// Display metadata for the three SNAP-IV 26 subscales. Maxima and clinical
// thresholds mirror the scoring source of truth
// (python-functions/rms_functions/rms_scoring.py): inattention and
// hyperactivity/impulsivity are each out of 27 with a >= 13 threshold;
// opposition is out of 24 with a >= 8 threshold.
const SUBSCALES = [
  { key: 'inattention', label: 'Inattention', max: 27, threshold: 13 },
  { key: 'hyperactivityImpulsivity', label: 'Hyperactivity / Impulsivity', max: 27, threshold: 13 },
  { key: 'oppositionDefiance', label: 'Opposition', max: 24, threshold: 8 },
];

function subscaleScore(results, key) {
  const n = Number(results?.[key]?.score);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Build the snapSnapshot object persisted in the decision payload, exactly as
 * displayed by this component:
 *   { parent:  { inattention, hyperactivityImpulsivity, opposition, completed },
 *     teacher: { inattention, hyperactivityImpulsivity, opposition, completed } }
 */
export function buildSnapSnapshot(formInstances) {
  const snapshotFor = (instanceId) => {
    const results = (formInstances || {})[instanceId]?.scaleResults;
    const completed = Boolean(results?.isComplete);
    return {
      inattention: completed ? subscaleScore(results, 'inattention') : 0,
      hyperactivityImpulsivity: completed
        ? subscaleScore(results, 'hyperactivityImpulsivity')
        : 0,
      opposition: completed ? subscaleScore(results, 'oppositionDefiance') : 0,
      completed,
    };
  };
  return {
    parent: snapshotFor(GUARDIAN_INSTANCE_ID),
    teacher: snapshotFor(SCHOOL_INSTANCE_ID),
  };
}

function RespondentColumn({ title, results }) {
  const completed = Boolean(results?.isComplete);
  return (
    <div className="flex-1 rounded-md border border-gray-200 bg-white p-3 text-left">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{title}</p>
      {completed ? (
        <ul className="mt-2 space-y-1.5">
          {SUBSCALES.map((s) => {
            const score = subscaleScore(results, s.key);
            const above = Boolean(results?.[s.key]?.aboveThreshold);
            return (
              <li key={s.key} className="flex items-center justify-between gap-2 text-sm">
                <span className="text-gray-700">{s.label}</span>
                <span className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 whitespace-nowrap">
                    {score}/{s.max}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${
                      above ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {above ? 'Above threshold' : 'Below threshold'}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="mt-2 text-sm font-medium text-gray-500">Not completed</p>
      )}
    </div>
  );
}

export default function SnapScoreSummary({ formInstances }) {
  const guardianResults = (formInstances || {})[GUARDIAN_INSTANCE_ID]?.scaleResults;
  const schoolResults = (formInstances || {})[SCHOOL_INSTANCE_ID]?.scaleResults;

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-left">
      <p className="text-sm font-medium text-gray-900">SNAP-IV 26 scores</p>
      <p className="mt-0.5 text-xs text-gray-500">
        Inattention and hyperactivity/impulsivity are each out of 27 (clinical threshold 13 or
        above); opposition is out of 24 (clinical threshold 8 or above).
      </p>
      <div className="mt-3 flex flex-col gap-3 sm:flex-row">
        <RespondentColumn title="Parent / Carer" results={guardianResults} />
        <RespondentColumn title="School / Teacher" results={schoolResults} />
      </div>
    </div>
  );
}
